// src/pages/Dashboard/StatsTable.js
import React from "react";
import useChatbotStats from "./useChatbotStats";
import { Box, CircularProgress, Typography, Table, TableHead, TableRow, TableCell, TableBody } from "@mui/material";
import BarChartIcon from '@mui/icons-material/BarChart';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const colors = {
  textPrimary: '#1e293b',
  textSecondary: '#475569',
  textTertiary: '#94a3b8',
  accent: '#8b5cf6',
};

export default function ChatbotStatsTable() {
  const { stats, error, loading } = useChatbotStats();

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 4 }}>
        <CircularProgress size={24} sx={{ color: colors.accent }} />
        <Typography sx={{ ml: 2, fontFamily, fontSize: '0.9rem', color: colors.textSecondary }}>
          Loading stats...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Box sx={{ p: 3, bgcolor: 'rgba(239,68,68,0.1)', borderRadius: '12px' }}>
        <Typography sx={{ fontFamily, color: '#dc2626' }}>{error}</Typography>
      </Box>
    );

  if (!stats)
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography sx={{ fontFamily, color: colors.textSecondary }}>
          No stats available for this chatbot.
        </Typography>
      </Box>
    );

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
        <Box sx={{
          width: 36,
          height: 36,
          borderRadius: '10px',
          bgcolor: 'rgba(139,92,246,0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <BarChartIcon sx={{ fontSize: '1.2rem', color: colors.accent }} />
        </Box>
        <Box>
          <Typography sx={{ fontFamily, fontSize: '1rem', fontWeight: 600, color: colors.textPrimary }}>
            Knowledge Base Statistics
          </Typography>
          <Typography sx={{ fontFamily, fontSize: '0.75rem', color: colors.textTertiary }}>
            Breakdown of your training data
          </Typography>
        </Box>
      </Box>

      {/* Table */}
      <Box sx={{
        borderRadius: '12px',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.06)',
      }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'rgba(139,92,246,0.05)' }}>
              <TableCell sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: colors.textPrimary, py: 1.5 }}>
                Category
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: colors.textPrimary }}>
                Characters
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: colors.textPrimary }}>
                Words
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontWeight: 600, fontSize: '0.8rem', color: colors.textPrimary }}>
                Tokens
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
              <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary, py: 1.5 }}>
                Private Documents
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: colors.textPrimary }}>
                {stats.privateDocs.total_page_characters.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: colors.textPrimary }}>
                {stats.privateDocs.total_page_words.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: colors.textPrimary }}>
                {stats.privateDocs.total_tokens.toLocaleString()}
              </TableCell>
            </TableRow>
            <TableRow sx={{ '&:hover': { bgcolor: 'rgba(0,0,0,0.02)' } }}>
              <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary, py: 1.5 }}>
                Website Links
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: colors.textPrimary }}>
                {stats.websiteLinks.total_page_characters.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: colors.textPrimary }}>
                {stats.websiteLinks.total_page_words.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', color: colors.textPrimary }}>
                {stats.websiteLinks.total_tokens.toLocaleString()}
              </TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: 'rgba(139,92,246,0.05)' }}>
              <TableCell sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.accent, py: 1.5 }}>
                Total (Knowledge Base)
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.accent }}>
                {stats.totalCharacters.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.accent }}>
                {stats.totalWords.toLocaleString()}
              </TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: colors.accent }}>
                {stats.totalTokens.toLocaleString()}
              </TableCell>
            </TableRow>
            <TableRow sx={{ borderTop: '2px solid rgba(0,0,0,0.06)' }}>
              <TableCell sx={{ fontFamily, fontSize: '0.85rem', color: colors.textSecondary, py: 1.5 }}>
                Chatbot Usage (Input + Output)
              </TableCell>
              <TableCell colSpan={2}></TableCell>
              <TableCell align="right" sx={{ fontFamily, fontSize: '0.85rem', fontWeight: 600, color: '#f59e0b' }}>
                {stats.tokens.total_total_token.toLocaleString()}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}
